import type {
  AgentAuditList,
  AgentDetail,
  AgentList,
  AgentUpdate,
  AgentSummary,
} from '@schwarz/contract';

import { apiSlice } from './api-slice.js';

/**
 * Agent configuration (S10).
 *
 * No polling. These records change when a person changes them — here or in the
 * platform's own dashboard — and polling an editor would mean pulling text out
 * from under whoever is typing. The stale case is handled where it actually
 * matters instead: a save is refused if the agent moved since it was loaded, and
 * the message says who moved it.
 */
export const agentsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAgents: build.query<AgentList, void>({
      query: () => ({ url: '/agents' }),
      providesTags: ['Agent'],
    }),

    getAgent: build.query<AgentDetail, string>({
      query: (id) => ({ url: `/agents/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Agent' as const, id }],
    }),

    /**
     * Save an edit.
     *
     * Invalidates the list as well as the record: `presetName` is what the list
     * shows, so a rename that left the list saying the old name would look like
     * the save had not worked.
     */
    updateAgent: build.mutation<AgentDetail, { id: string; body: AgentUpdate }>({
      query: ({ id, body }) => ({ url: `/agents/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Agent' as const, id }, 'Agent'],
    }),

    getAgentAudit: build.query<AgentAuditList, string>({
      query: (id) => ({ url: `/agents/${id}/audit` }),
      providesTags: (_result, _error, id) => [{ type: 'Agent' as const, id }],
    }),
  }),
});

/**
 * The shared cache key for a save.
 *
 * RTK Query keys mutation state by request id, so two components calling the
 * same hook see two unrelated states. A fixed key makes them one — which is how
 * the sidebar knows a save is in flight without a save button of its own, and
 * without a global "busy" flag that every future feature would have to remember
 * to set and clear.
 */
export const AGENT_SAVE_CACHE_KEY = 'agent-save';

export const {
  useGetAgentsQuery,
  useGetAgentQuery,
  useUpdateAgentMutation,
  useGetAgentAuditQuery,
} = agentsApi;

/** What the list calls each agent, in the operator's terms rather than the enum's. */
export function agentRoleLabel(agent: Pick<AgentSummary, 'role'>): string {
  return agent.role === 'OUTBOUND' ? 'Makes the calls' : 'Classifies the calls';
}
